import { MechanicValidator } from './MechanicValidator.ts';
import { ArrayUtils } from '@colonist/utils';
import { PieceTypes } from '@colonist/game-base-types';
import { PlayerColor, TileEdgeType } from '@colonist/game-states';
import { getCardsForPiece } from '@colonist/game-card-types';
import { MechanicValidatorPirate } from './MechanicValidatorPirate.ts';
export class MechanicValidatorShip extends MechanicValidator {
    getShipPlayerState(player) {
        if (player == PlayerColor.None) {
            return {
                bankShipAmount: 0
            };
        }
        const mechanicShipPlayerState = this.mechanicShipState[player];
        if (mechanicShipPlayerState === undefined) throw new Error(`No ship player state for player ${player}`);
        return mechanicShipPlayerState;
    }
    hasResourcesAndPiecesForShip(player, useResources) {
        const shipPlayerState = this.getShipPlayerState(player);
        if (shipPlayerState.bankShipAmount <= 0) return {
            canTakeAction: false,
            errorMessageIfCantTakeAction: {
                key: 'strings:game.errors.notEnoughShipPieces'
            }
        };
        if (!useResources) return {
            canTakeAction: true
        };
        const resourcesForShip = getCardsForPiece(PieceTypes.Ship);
        const playerController = this.playerValidators.get(player);
        if (playerController == undefined) return {
            canTakeAction: false,
            errorMessageIfCantTakeAction: {
                key: 'strings:game.errors.noPlayer'
            }
        };
        const hasResourcesForShip = playerController.hasResourceCardsInHand(resourcesForShip);
        if (!hasResourcesForShip) return {
            canTakeAction: false,
            errorMessageIfCantTakeAction: {
                key: 'strings:game.errors.notEnoughResources'
            }
        };
        return {
            canTakeAction: true
        };
    }
    canPlayerPlaceShip(player, useResources) {
        const playerController = this.playerValidators.get(player);
        if (playerController == undefined) return {
            canTakeAction: false,
            errorMessageIfCantTakeAction: {
                key: 'strings:game.errors.noPlayer'
            },
            validHoverLocations: []
        };
        const where = this.whereCanPlayerPlaceShips(player);
        const doesPlayerHaveResourcesAndPiecesForShip = this.hasResourcesAndPiecesForShip(player, useResources);
        doesPlayerHaveResourcesAndPiecesForShip.validHoverLocations = where;
        if (!doesPlayerHaveResourcesAndPiecesForShip.canTakeAction) return doesPlayerHaveResourcesAndPiecesForShip;
        if (where.length <= 0) return {
            canTakeAction: false,
            errorMessageIfCantTakeAction: {
                key: 'strings:game.errors.noWhereToPlaceShip'
            },
            validHoverLocations: where
        };
        return {
            canTakeAction: true,
            validHoverLocations: where
        };
    }
    whereCanPlayerPlaceShips(player, edgeToSkip = undefined) {
        const placesToBuildShip = [];
        for (const corner of this.mapValidator.tileState.tileCorners){
            if (corner.owner != player) continue;
            if (corner.buildingType == undefined) continue;
            if (this.mapValidator.isCornerKnightAndIsKnightNotTouchingEdgeType(player, corner, TileEdgeType.Ship)) continue;
            for (const edge of corner.hexCorner.touchingEdges()){
                var _this_pirateValidator;
                const tileEdge = this.mapValidator.tileState.getTileEdge(edge);
                if (tileEdge == undefined) continue;
                if (!this.mapValidator.tileState.isEdgeTouchingSea(tileEdge)) continue;
                if (tileEdge.owner != undefined) continue;
                if (tileEdge.type != undefined) continue;
                if ((_this_pirateValidator = this.pirateValidator) === null || _this_pirateValidator === void 0 ? void 0 : _this_pirateValidator.isEdgeTouchingTileWithPirate(tileEdge)) continue;
                const edgeIndex = this.mapValidator.tileState.getTileEdgeIndex(tileEdge.hexEdge);
                if (edgeIndex == undefined) continue;
                if (placesToBuildShip.includes(edgeIndex)) continue;
                placesToBuildShip.push(edgeIndex);
            }
        }
        for (const edge of this.mapValidator.tileState.tileEdges){
            if (edge.owner != player) continue;
            if (edge.type != TileEdgeType.Ship) continue;
            const tileEdgeIndex = this.mapValidator.tileState.getTileEdgeIndex(edge.hexEdge);
            if (tileEdgeIndex == edgeToSkip) continue;
            for (const touchingCorners of edge.hexEdge.endPoints()){
                const tileCorner = this.mapValidator.tileState.getTileCorner(touchingCorners);
                if (tileCorner == undefined) continue;
                if (tileCorner.owner != undefined) continue;
                for (const touchingEdges of touchingCorners.touchingEdges()){
                    var _this_pirateValidator1;
                    const tileEdge = this.mapValidator.tileState.getTileEdge(touchingEdges);
                    if (tileEdge == undefined) continue;
                    if (tileEdge.type != undefined) continue;
                    if (!this.mapValidator.tileState.isEdgeTouchingSea(tileEdge)) continue;
                    if (tileEdge.owner != undefined) continue;
                    if ((_this_pirateValidator1 = this.pirateValidator) === null || _this_pirateValidator1 === void 0 ? void 0 : _this_pirateValidator1.isEdgeTouchingTileWithPirate(tileEdge)) continue;
                    const edgeIndex = this.mapValidator.tileState.getTileEdgeIndex(tileEdge.hexEdge);
                    if (edgeIndex == undefined) continue;
                    if (placesToBuildShip.includes(edgeIndex)) continue;
                    placesToBuildShip.push(edgeIndex);
                }
            }
        }
        const playerController = this.playerValidators.get(player);
        if (playerController == undefined) return placesToBuildShip;
        const shipPlayerState = this.getShipPlayerState(playerController.color);
        var _shipPlayerState_restrictedShipPlacements;
        for (const restrictedPlacement of (_shipPlayerState_restrictedShipPlacements = shipPlayerState.restrictedShipPlacements) !== null && _shipPlayerState_restrictedShipPlacements !== void 0 ? _shipPlayerState_restrictedShipPlacements : []){
            ArrayUtils.removeElementFromArray(placesToBuildShip, restrictedPlacement);
        }
        return placesToBuildShip;
    }
    whereCanPlayerPlaceBeginningShips(player) {
        const placeToBuildShip = [];
        for (const corner of this.mapValidator.tileState.tileCorners){
            if (corner.owner != player) continue;
            if (corner.buildingType == undefined) continue;
            if (!this.mapValidator.areAdjacentEdgesEmpty(corner)) continue;
            for (const edge of corner.hexCorner.touchingEdges()){
                var _this_pirateValidator;
                const tileEdge = this.mapValidator.tileState.getTileEdge(edge);
                if (tileEdge == undefined) continue;
                if (!this.mapValidator.tileState.isEdgeTouchingSea(tileEdge)) continue;
                if ((_this_pirateValidator = this.pirateValidator) === null || _this_pirateValidator === void 0 ? void 0 : _this_pirateValidator.isEdgeTouchingTileWithPirate(tileEdge)) continue;
                const tileIndex = this.mapValidator.tileState.getTileEdgeIndex(tileEdge.hexEdge);
                if (tileIndex == undefined) continue;
                placeToBuildShip.push(tileIndex);
            }
        }
        return placeToBuildShip;
    }
    constructor(playerValidators, mapValidator, mechanicShipState, mechanicPirateState){
        super(playerValidators, mapValidator);
        this.mechanicShipState = mechanicShipState;
        if (mechanicPirateState != undefined) this.pirateValidator = new MechanicValidatorPirate(mapValidator, mechanicPirateState);
    }
}
